import React, { useMemo, useState } from "react";
import { priorityColors } from "./chartTheme";

const COLUMNS = [
  { key: "age_days", label: "Age" },
  { key: "title", label: "PR" },
  { key: "repository", label: "Repo" },
  { key: "author_github_username", label: "Author" },
  { key: "assigned_reviewer", label: "Reviewer" },
  { key: "complexity_score", label: "Complexity" },
  { key: "priority", label: "Priority" },
];

/**
 * Real sortable table, not a chart — the accessibility guidance calls for a
 * data-table alternative to the other charts, and a stale-PR list is
 * naturally tabular data anyway. Defaults to oldest-first (matches the
 * backend's own ORDER BY), same priority color map as PrCard.js.
 */
function AgingPrTable({ data }) {
  const [sortKey, setSortKey] = useState("age_days");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-rf-text-muted text-sm">
        No PRs older than the staleness threshold — nothing to catch up on.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-rf-border">
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                className="pr-4 py-2 text-xs text-rf-text-muted font-medium uppercase tracking-wider cursor-pointer hover:text-rf-text-secondary select-none whitespace-nowrap"
              >
                {col.label}
                {sortKey === col.key && (sortDir === "asc" ? " ↑" : " ↓")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((pr) => (
            <tr key={pr.pr_id} className="border-b border-rf-border/40 last:border-0">
              <td className="pr-4 py-2.5 font-mono text-rf-text-primary whitespace-nowrap">
                {pr.age_days}d
              </td>
              <td className="pr-4 py-2.5 max-w-[280px] truncate">
                <a
                  href={pr.github_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-rf-text-primary hover:text-rf-accent-blue transition-colors"
                >
                  {pr.title}
                </a>
              </td>
              <td className="pr-4 py-2.5 text-rf-text-secondary font-mono text-xs whitespace-nowrap">
                {pr.repository}
              </td>
              <td className="pr-4 py-2.5 text-rf-text-secondary whitespace-nowrap">
                @{pr.author_github_username}
              </td>
              <td className="pr-4 py-2.5 text-rf-text-secondary whitespace-nowrap">
                {pr.assigned_reviewer ? `@${pr.assigned_reviewer}` : "—"}
              </td>
              <td className="pr-4 py-2.5 font-mono text-rf-text-primary whitespace-nowrap">
                {pr.complexity_score?.toFixed(1) ?? "—"}
              </td>
              <td className="pr-4 py-2.5 whitespace-nowrap">
                <span
                  className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full"
                  style={{
                    color: priorityColors[pr.priority] || priorityColors.medium,
                    background: `${priorityColors[pr.priority] || priorityColors.medium}18`,
                  }}
                >
                  {pr.priority}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AgingPrTable;
